import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

const comparisonRowSchema = z.object({
  feature: z.string().default("Feature"),
  values: z.array(z.union([z.boolean(), z.string()])).default([true, true]),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Compare Your Options"),
  subheading: z.string().optional(),
  columnHeaders: z.array(z.string()).default(["Standard", "Premium"]),
  rows: z
    .array(comparisonRowSchema)
    .default([
      { feature: "Initial Consultation", values: [true, true] },
      { feature: "Written Summary", values: [true, true] },
      { feature: "Priority Scheduling", values: [false, true] },
      { feature: "Extended Follow-Up", values: [false, true] },
      { feature: "Dedicated Advisor", values: [false, true] },
      { feature: "Quarterly Reviews", values: [false, true] },
    ]),
  highlightColumn: z.number().int().optional(),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().default("/book"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

function CellValue({ value }: { value: boolean | string }) {
  if (typeof value === "boolean") {
    return value ? (
      <span aria-label="Included" className="text-current opacity-80 font-bold text-lg">&#10003;</span>
    ) : (
      <span aria-label="Not included" className="text-text-muted text-lg">&mdash;</span>
    );
  }
  return <span>{value}</span>;
}

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  if (safe.rows.length === 0) return null;

  return (
    <SectionShell globals={safe} bare>
      <div className="max-w-5xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80 max-w-2xl mx-auto"
              data-prop="subheading"
            />
          )}
        </div>

        {/* Table with horizontal scroll on mobile */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[400px]">
            <thead>
              <tr className="border-b-2 border-current/15">
                <th className="py-3 pr-4 font-semibold">Feature</th>
                {safe.columnHeaders.map((header, ci) => (
                  <th
                    key={ci}
                    className={`py-3 px-4 font-semibold text-center ${
                      safe.highlightColumn === ci
                        ? "bg-primary text-white rounded-t-lg"
                        : ""
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safe.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-current/10">
                  <td className="py-4 pr-4 font-medium">
                    <RichText
                      html={row.feature}
                      data-prop={`rows.${ri}.feature`}
                    />
                  </td>
                  {safe.columnHeaders.map((_, ci) => {
                    const value = ci < row.values.length ? row.values[ci] : false;
                    return (
                      <td
                        key={ci}
                        className={`py-4 px-4 text-center ${
                          safe.highlightColumn === ci
                            ? "bg-primary/5"
                            : ""
                        }`}
                      >
                        <CellValue value={value} />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {safe.ctaLabel && (
                <tr>
                  <td className="py-4 pr-4" />
                  {safe.columnHeaders.map((_, ci) => (
                    <td key={ci} className={`py-4 px-4 text-center ${safe.highlightColumn === ci ? "bg-primary/5" : ""}`}>
                      <a
                        href={safe.ctaHref}
                        className={`inline-block font-semibold px-6 py-2.5 rounded-lg text-center transition-opacity hover:opacity-90 ${
                          safe.highlightColumn === ci
                            ? "bg-white text-primary border border-primary"
                            : "bg-primary text-white"
                        }`}
                      >
                        {safe.ctaLabel}
                      </a>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SectionShell>
  );
}
